import { isEqual, uniqueId } from "lodash";
import * as React from "react";
import { JSX } from "react";
import { Link } from "react-router-dom";
import { ExportType } from "@app/exports/enumerations";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { CalculationMethod, EntityTypeEnum, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, TimeFrequency } from "@app/projects/enumerators";
import { getPrimaryAndSecondaryRunConfigurations } from "@app/projects/network-assessment/adequacy/utility";
import { DisplayOptions } from "@app/projects/network-assessment/DisplayOptions";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { ServicingProviderResult } from "@app/projects/results/servicing-provider";
import NextClosestProvider from "@app/projects/results/servicing-provider/nextClosestProvider";
import NextClosestProviderResult from "@app/projects/results/servicing-provider/nextClosestProviderResult";
import ServicingProvider from "@app/projects/results/servicing-provider/servicingProvider";
import {
  CareJourneyScoresValueEnum,
  EffectivenessEfficiencyValueEnum,
  MotiveScoreValueEnum,
  NewPatientClaimsEnum,
  TopCondition,
  TopProcedure
} from "@app/projects/servicing-provider";
import { dismiss } from "@app/projects/servicing-provider/DistanceResultToast";
import NextClosestProviderService from "@app/projects/servicing-provider/nextClosestProviderService";
import ContractEntityProfile from "@app/projects/servicing-provider/provider-affiliations/ContractEntityProfile";
import { WrappedTerminateProvidersSimulationPanel } from "@app/projects/servicing-provider/provider-termination/TerminateProvidersSimulationPanel";
import { buildAdequacyProvidersColumnConfig, getCargoDataForProvider, getEntityIcon } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { TerminatePanelMode, WrappedTerminateProvidersPanel as TerminateProvidersPanel } from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { GainsPercentEnum, NPF } from "@app/projects/servicing-provider/types";
import { ZipContextEventHandlersObject } from "@app/projects/zip-projects/interfaces";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";
import { getSearchParametersFromFilterSelections } from "@app/projects/zip-projects/utility";
import { withZipContextEventHandlers, withZipContextState } from "@app/projects/zip-projects/ZipProjectContext";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { RegionGranularity } from "@app/region-sets";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ResourceType } from "@app/resources";
import { ConfidenceTierBadge } from "@app/shared/ConfidenceTierBadge";
import { FlyoutContextOutgoingProps, FlyoutLink, withFlyoutContext } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ModalLink } from "@app/shared/Modals";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import Features from "@app/feature-flags/features";
import ProjectService from "@app/projects/projectService";
import AccessSummary from "@app/projects/results/adequacy/accessSummary";
import ConfidenceTierSearchFilters from "@app/projects/servicing-provider/provider-claims/ConfidenceTierSearchFilters";
import ProviderClaimsFilters from "@app/projects/servicing-provider/provider-claims/ProviderClaimsFilters";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import formatPhoneNumber from "@app/shared/formatPhoneNumber";
import { QESButton } from "@app/shared/qes-standards/Button";
import { SearchBox } from "@app/shared/SearchBox";
import { Toggle } from "@app/shared/Toggle";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import { handleDistanceClick } from "@app/shared/utility/handleDistanceClick";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import { restrictByFields } from "@app/viewable-fields/utilities";
import { TableType } from "@app/viewable-fields/viewableFields";
import { ViewableFieldsContextProps, withViewableFields } from "@app/viewable-fields/ViewableFieldsContext";

type NetworkProvidersPanelProps = CountyProjectContextProps & ZipContextState & ViewableFieldsContextProps & FeatureFlagContextProps & UserContextProps & FlyoutContextOutgoingProps &
  ZipContextEventHandlersObject &
{
  accessSummary: AccessSummary;
  className?: string;
  exportEndpoint: string;
  nextClosestProviders: boolean;
  projectURL: string;
  regionSetId: number;
  showPatternOfCareNetworkProviders?: boolean; // passed through to create report/exportdata
  specialtyGroupCode: string;
};

//type TableModel = ServicingProvider & NetworkServicingProviderTerminationModel & NextClosestProvider;

interface NetworkProvidersPanelState {
  accessStandardText: string;
  columnConfig: ColumnConfig;
  displayOptionsDirty: boolean;
  isFiltered: boolean;
  isOpen: boolean;
  lastSearchValue: string;
  lastSelectedCareJourneyCostValues: CareJourneyScoresValueEnum[];
  lastSelectedCareJourneyQualityValues: CareJourneyScoresValueEnum[];
  lastSelectedConditions: string[];
  lastSelectedEffectivenessValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedEfficiencyValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
  lastSelectedLocationConfidenceValues: LocationConfidenceScoresLabelEnum[];
  lastSelectedMotiveAppropriatenessValues: MotiveScoreValueEnum[];
  lastSelectedNewPatientClaimsValues: NewPatientClaimsEnum[];
  lastSelectedPgrConfidenceTiers: number[];
  lastSelectedProcedures: string[];
  lastSelectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum;
  lastSelectedVolumeLabels: ClaimsActivityVolumeLabelEnum[];
  lastSortedCargo: string;
  lastSortedColumn: keyof NetworkProviderTableDataWithCargo;
  lastSortedDirection: SortDirections;
  loadStatus: LoadStatus;
  offset: number;
  selectedProviders: ServicingProvider[] | NetworkServicingProviderTerminationModel[];
  showAverageTime: boolean;
  showAverageDistance: boolean;
  sortedRecords: ServicingProvider[];
  tableDataProviders: NetworkProviderTableDataWithCargo[];
  tableDataTerminations: NetworkProviderTableDataWithCargo[];
  tableType: TableType;
  tableTypeChanged: boolean;
  termedRecords: NetworkServicingProviderTerminationModel[];
  topConditions: DropdownItem<TopCondition>[];
  topProcedures: DropdownItem<TopProcedure>[];
  totalCount: number;
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

//enum NCPF {
//  AverageDistance = "AverageDistance",
//  AverageTime = "AverageTime",
//  IsServicing = "IsServicing",
//  MemberCount = "MemberCount",
//}

interface NetworkProviderTableData {
  [wafflesKeyColumnName]: string;
  acceptingNewPatients?: string;
  acceptsMedicareAssignment?: string;
  address: JSX.Element;
  availability?: JSX.Element;
  contractType?: string;
  countyName?: string;
  countyState?: string;
  countyFIPS?: string;
  countySSA?: string;
  credentials?: string;
  letterOfIntent?: string;
  daysSinceLastAttested?: string;
  displayName: JSX.Element;
  effectivenessRank?: string;
  efficiencyRank?: string;
  employmentStatus?: string;
  externLocationId?: string;
  externProviderId?: string;
  externRowId?: string;
  externSpecialtyCode?: string;
  externZip?: string;
  firstName?: string;
  gender?: string;
  geoInfo?: string;
  lastName?: string;
  latitude?: number;
  longitude?: number;
  medGroupAffiliation?: string;
  medicareCCN?: string;
  medicareCertifiedBeds?: number;
  middleName?: string;
  averageDistance?: number;
  averageTime?: number;
  isServicing?: string;
  memberCount?: number;
  nppesCertificationDate?: string;
  nppesLastUpdatedDate?: string;
  prefix?: string;
  servesPercent?: string;
  specialtyCode?: string;
  specialty?: string;
  suffix?: string;
  taxonomyCode?: string;
  terminationReason?: JSX.Element;
  underContract?: string;
  mipsScore?: string;
  commercialClaimsVolume?: string;
  commercialNewPatientClaims?: string;
  medicaidClaimsVolume?: string;
  medicaidNewPatientClaims?: string;
  medicareClaimsVolume?: string;
  medicareNewPatientClaims?: string;
  vaClaimsVolume?: string;
  vaNewPatientClaims?: string;
  tricareClaimsVolume?: string;
  tricareNewPatientClaims?: string;
  totalClaimsVolume?: string;
  totalNewPatientClaims?: string;
  LocationConfidenceScore?: string;
  motiveScore?: string;
  careJourneyScoresCostScore?: string;
  careJourneyScoresQualityScore?: string;
  recordingCriteria?: string;
  providerGrouping?: string;
  providerValidations?: string | JSX.Element;
  specialtyPredictionModel?: string;
  pgrConfidenceTierDisplay?: JSX.Element | string;
}

type NetworkProviderTableDataWithCargo = {
  [cargoKey: string]: string | number | JSX.Element;
} & NetworkProviderTableData;

class NetworkProvidersPanel extends React.Component<NetworkProvidersPanelProps, NetworkProvidersPanelState> {
  private readonly batchCount: number = 100;
  private networkTotalCount = 0;
  private readonly projectService = new ProjectService();
  private readonly nextClosestProviderService = new NextClosestProviderService();
  private readonly providerTerminationService = new ProviderTerminationService();
  private readonly toastId = uniqueId("distance-toast-");
  private isZipProject: boolean;
  private cargoColumns = new Set<string>();
  private providerPlurality = "providers";
  // Prevents buildServicingProviders from auto-switching back to ProviderTerminations when the
  // user has explicitly toggled to the NetworkProviders view and all providers happen to be termed.
  private isUserInitiatedTableTypeSwitch = false;
  // Guards against a slower, superseded request resolving after a newer one and overwriting its results.
  private latestRequestSequence: number = 0;

  constructor(props: NetworkProvidersPanelProps) {
    super(props);

    this.state = {
      accessStandardText: "",
      columnConfig: {},
      displayOptionsDirty: false,
      isFiltered: false,
      isOpen: false,
      lastSearchValue: "",
      lastSelectedCareJourneyCostValues: undefined,
      lastSelectedCareJourneyQualityValues: undefined,
      lastSelectedConditions: [],
      lastSelectedEffectivenessValues: undefined,
      lastSelectedEfficiencyValues: undefined,
      lastSelectedLineOfBusiness: undefined,
      lastSelectedLocationConfidenceValues: undefined,
      lastSelectedMotiveAppropriatenessValues: undefined,
      lastSelectedNewPatientClaimsValues: undefined,
      lastSelectedPgrConfidenceTiers: [],
      lastSelectedProcedures: [],
      lastSelectedVolumeLabels: undefined,
      lastSortedCargo: undefined,
      lastSortedColumn: undefined,
      lastSortedDirection: SortDirections.None,
      loadStatus: "shouldLoad",
      offset: 0,
      selectedProviders: [],
      showAverageDistance: false,
      showAverageTime: false,
      sortedRecords: [],
      tableDataProviders: [],
      tableDataTerminations: [],
      tableType: props.nextClosestProviders ? TableType.NextClosestNetworkProviders : TableType.NetworkProviders,
      tableTypeChanged: false,
      termedRecords: [],
      topConditions: [],
      topProcedures: [],
      totalCount: 0,
    };

    this.isZipProject = this.props.project.resource.resourceType === ResourceType.ZipAnalysisProject;
  }

  public async componentDidMount() {
    const newColumnConfig = this.getColumnConfig();
    this.setState({
      columnConfig: newColumnConfig,
    });
  }

  public async componentDidUpdate(prevProps: NetworkProvidersPanelProps, prevState: NetworkProvidersPanelState) {
    if (this.props.shouldRefreshNetworkProviders) {
      this.props.setShouldRefreshNetworkProviders(false);
      // A zip/specialty filter change means any search/filter state left over from the
      // previous selection no longer applies to the new provider set. Clearing it here
      // lets the tableType reset below (based on termedRecords.length) fire reliably,
      // instead of being permanently blocked by a stale isFiltered from the prior ZIP.
      this.setState({
        lastSearchValue: "",
        tableTypeChanged: true,
        lastSelectedCareJourneyCostValues: undefined,
        lastSelectedCareJourneyQualityValues: undefined,
        lastSelectedConditions: [],
        lastSelectedEffectivenessValues: undefined,
        lastSelectedEfficiencyValues: undefined,
        lastSelectedLineOfBusiness: undefined,
        lastSelectedLocationConfidenceValues: undefined,
        lastSelectedMotiveAppropriatenessValues: undefined,
        lastSelectedNewPatientClaimsValues: undefined,
        lastSelectedProcedures: [],
        lastSelectedSpecialtyPredictionModelFilter: undefined,
        lastSelectedVolumeLabels: undefined,
      }, () => {
        this.buildServicingProviders(true, undefined, undefined, undefined, true);
      });
    }

    if (prevState.loadStatus === "loaded" && this.state.loadStatus === "shouldLoad") {
      await this.buildServicingProviders(true);
    }

    const isFiltered = !!this.state.lastSearchValue ||
      !!this.state.lastSelectedVolumeLabels ||
      !!this.state.lastSelectedLineOfBusiness ||
      !!this.state.lastSelectedEffectivenessValues ||
      !!this.state.lastSelectedEfficiencyValues ||
      !!this.state.lastSelectedLocationConfidenceValues ||
      !!this.state.lastSelectedMotiveAppropriatenessValues ||
      !!this.state.lastSelectedNewPatientClaimsValues ||
      this.state.lastSelectedProcedures.length > 0 ||
      !!this.state.lastSelectedCareJourneyCostValues ||
      !!this.state.lastSelectedCareJourneyQualityValues ||
      this.state.lastSelectedConditions.length > 0 ||
      this.state.lastSelectedPgrConfidenceTiers.length > 0;

    if (this.state.tableType === TableType.ProviderTerminations && !isFiltered && this.state.termedRecords.length === 0) {
      this.setState({
        tableType: TableType.NetworkProviders,
        // totalCount was set to termedProviders.length (0) while tableType was ProviderTerminations;
        // switch it back to the network total so the accordion title doesn't show "(N of 0)".
        totalCount: this.networkTotalCount || this.state.sortedRecords.length,
      });
    }

    if (!isEqual(prevProps.viewableFieldsByTableType.get(this.state.tableType), this.props.viewableFieldsByTableType.get(this.state.tableType)) ||
      prevState.showAverageDistance !== this.state.showAverageDistance ||
      prevState.showAverageTime !== this.state.showAverageTime ||
      !isEqual(prevProps.runConfiguration, this.props.runConfiguration) ||
      prevState.tableType !== this.state.tableType) {
      const newColumnConfig = this.getColumnConfig();
      this.setState({
        columnConfig: newColumnConfig,
      });
      }
  }

  public componentWillUnmount() {
    dismiss(this.toastId);
  }

  public render() {
    const initialLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const title = this.getTitle();
    this.providerPlurality = this.state.selectedProviders.length === 1 ? "provider" : "providers";

    return (
      <AccordionContainer className={this.props.className}>
        <Accordion
          className={this.props.className}
          defaultOpen={false}
          title={title}
          onPanelWillOpen={initialLoading ? async () => await this.buildServicingProviders() : null}
          onPanelDidOpen={this.handleOnOpen}
          onPanelDidClose={this.handleOnClose}
          testId={this.props.nextClosestProviders ? "next-closest-network-providers-panel" : "network-providers-panel"}
        >
          {this.renderSearchField()}
          {this.renderProviderTable()}
        </Accordion>
      </AccordionContainer>
    );
  }

  private buildServicingProviders = async (
    forceLoad?: boolean,
    selectedColumn?: keyof NetworkProviderTableDataWithCargo,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    resetOffset?: boolean,
    searchValue?: string,
    selectedEffectivenessValues: EffectivenessEfficiencyValueEnum[] = this.state.lastSelectedEffectivenessValues,
    selectedEfficiencyValues: EffectivenessEfficiencyValueEnum[] = this.state.lastSelectedEfficiencyValues,
    selectedVolumeLabels: ClaimsActivityVolumeLabelEnum[] = this.state.lastSelectedVolumeLabels,
    selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum = this.state.lastSelectedLineOfBusiness,
    selectedLocationConfidenceValues: LocationConfidenceScoresLabelEnum[] = this.state.lastSelectedLocationConfidenceValues,
    selectedMotiveAppropriatenessValues: MotiveScoreValueEnum[] = this.state.lastSelectedMotiveAppropriatenessValues,
    selectedNewPatientClaimsValues: NewPatientClaimsEnum[] = this.state.lastSelectedNewPatientClaimsValues,
    selectedTopProcedures: string[] = this.state.lastSelectedProcedures,
    selectedCareJourneyCostValues: CareJourneyScoresValueEnum[] = this.state.lastSelectedCareJourneyCostValues,
    selectedCareJourneyQualityValues: CareJourneyScoresValueEnum[] = this.state.lastSelectedCareJourneyQualityValues,
    selectedTopConditions: string[] = this.state.lastSelectedConditions,
    selectedSpecialtyPredictionModelFilter: SpecialtyPredictionModelEnum = this.state.lastSelectedSpecialtyPredictionModelFilter,
    selectedPgrConfidenceTiers: number[] = this.state.lastSelectedPgrConfidenceTiers,
    shouldRefreshNetworkList?: boolean
  ) => {
    // Guards against a slower, superseded request resolving after a newer one and overwriting its results.
    let requestSequence: number;
    try {
      // we fetch these here to make sure the viewable fields are up to date, e.g. if different timeslices
      // have different provider sets
      this.props.getByTableType(this.state.tableType);

      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad" || forceLoad
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : null;
      let noServicingProviders: boolean;

      // we can't assign this.state.lastSearchValue as the default in the parameter list because an empty string is falsey gets replaced
      const searchString = searchValue === undefined ? this.state.lastSearchValue : searchValue;

      if (nextLoadStatus) {
        this.setState((prevState) => ({
          ...prevState,
          loadStatus: nextLoadStatus,
        }));

        requestSequence = ++this.latestRequestSequence;

        let offset = this.state.offset;
        if ((selectedColumn && selectedColumn !== this.state.lastSortedColumn) || (sortDirection !== undefined && sortDirection !== this.state.lastSortedDirection) || resetOffset) {
          offset = 0;
        }

        if (this.props.project.showPciData &&
          selectedLineOfBusiness === undefined &&
          selectedVolumeLabels === undefined &&
          selectedEffectivenessValues === undefined &&
          selectedEfficiencyValues === undefined &&
          selectedLocationConfidenceValues === undefined &&
          selectedMotiveAppropriatenessValues === undefined &&
          selectedNewPatientClaimsValues === undefined &&
          selectedCareJourneyCostValues === undefined &&
          selectedCareJourneyQualityValues === undefined
        ) {
          // pci-specific defaults for on load
          selectedEffectivenessValues = Object.values(EffectivenessEfficiencyValueEnum);
          selectedEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum);
          selectedLineOfBusiness = ClaimsActivityLineOfBusinessTypeEnum.Total;
          selectedVolumeLabels = [...Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => x)];
          selectedLocationConfidenceValues = Object.values(LocationConfidenceScoresLabelEnum);
          selectedMotiveAppropriatenessValues = Object.values(MotiveScoreValueEnum);
          selectedNewPatientClaimsValues = Object.values(NewPatientClaimsEnum);
          selectedCareJourneyCostValues = Object.values(CareJourneyScoresValueEnum);
          selectedCareJourneyQualityValues = Object.values(CareJourneyScoresValueEnum);
        }

        // Filters only apply to the Network list while it is shown, or when the Terminated view falls back to it.
        const fetchNetworkProviders = (applyNetworkFilters: boolean) => this.projectService.servicingProviders(
          this.props.project.projectId,
          this.props.regionSetId,
          this.props.runConfiguration.projectRunConfigurationId,
          this.props.specialtyGroupCode,
          ProviderNetworkTypeEnum.Network,
          this.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : this.props.runConfiguration.networkAssessmentAnalysisType,
          GainsPercentEnum.All,
          offset,
          this.batchCount,
          selectedColumn as string,
          sortDirection,
          columnSubKey,
          // if we're viewing termed, and there's a search, we want the user to be able to just toggle off termed
          // and it'll go back to the full list of servicing, instead of a filtered list with no indication of being searched
          applyNetworkFilters ? searchString : "",
          applyNetworkFilters ? selectedEffectivenessValues : undefined,
          applyNetworkFilters ? selectedEfficiencyValues : undefined,
          applyNetworkFilters ? selectedVolumeLabels : undefined,
          applyNetworkFilters ? selectedLineOfBusiness : undefined,
          applyNetworkFilters ? selectedLocationConfidenceValues : undefined,
          applyNetworkFilters ? selectedMotiveAppropriatenessValues : undefined,
          applyNetworkFilters ? selectedNewPatientClaimsValues : undefined,
          selectedTopProcedures,
          applyNetworkFilters ? selectedCareJourneyCostValues : undefined,
          applyNetworkFilters ? selectedCareJourneyQualityValues : undefined,
          selectedTopConditions,
          applyNetworkFilters ? selectedSpecialtyPredictionModelFilter : undefined,
          applyNetworkFilters ? selectedPgrConfidenceTiers : undefined
        );

        let servicingProviderResult: ServicingProviderResult = this.props.nextClosestProviders
          ? await this.nextClosestProviderService.nextClosestNetworkProviders(
            this.props.project.projectId,
            this.props.regionSetId,
            this.props.runConfiguration.projectRunConfigurationId,
            this.props.specialtyGroupCode,
            this.props.runConfiguration.networkAssessmentAnalysisType,
            offset,
            this.batchCount,
            selectedColumn as string, // I don't know why selectedColum: keyof NetworkProviderTableDataWithCargo is being parsed as type string | number
            sortDirection,
            columnSubKey,
            searchString,
            selectedEffectivenessValues,
            selectedEfficiencyValues,
            selectedVolumeLabels,
            selectedLineOfBusiness,
            selectedLocationConfidenceValues,
            selectedMotiveAppropriatenessValues,
            selectedNewPatientClaimsValues,
            selectedTopProcedures,
            selectedCareJourneyCostValues,
            selectedCareJourneyQualityValues,
            selectedTopConditions,
            selectedSpecialtyPredictionModelFilter,
            selectedPgrConfidenceTiers
          )
          : await fetchNetworkProviders(this.state.tableType === TableType.NetworkProviders);

        if (requestSequence !== this.latestRequestSequence) {
          return;
        }

        if (this.props.project.showPciData) {
          if (!isEqual(servicingProviderResult?.topConditions.map(x => x.code), this.state.topConditions.map(x => x.key.code))) {
            const mappedTopConditions: DropdownItem<TopCondition>[] = servicingProviderResult?.topConditions.map(x => {
              return { key: x, name: `${x.code} - ${x.description}` }
            });
            this.setState({ topConditions: mappedTopConditions });
          }

          if (!isEqual(servicingProviderResult?.topProcedures.map(x => x.code), this.state.topProcedures.map(x => x.key.code))) {
            const mappedTopProcedures: DropdownItem<TopProcedure>[] = servicingProviderResult?.topProcedures.map(x => {
              return { key: x, name: `${x.code} - ${x.description}` }
            });
            this.setState({ topProcedures: mappedTopProcedures });
          }
        }

        let loadStatus: LoadStatus = "error";
        let newRecords: ServicingProvider[];

        let termedProviders: NetworkServicingProviderTerminationModel[] = [];
        let fellBackToNetwork = false;
        if ((this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) || this.props.user.roles.includes(Roles.TERM_PROVIDER_VIEWER))
          && !this.props.nextClosestProviders) {
          // Only show terminated provider icons on the latest published timeslice
          if (this.props.runConfiguration.isLatestPublished) {
            termedProviders = await this.providerTerminationService.getTerminatedProviders(
              this.props.project.projectId,
              this.props.runConfiguration.projectRunConfigurationId,
              this.props.regionSetId,
              this.props.specialtyGroupCode,
              this.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : this.props.runConfiguration.networkAssessmentAnalysisType,
              selectedColumn as string,
              sortDirection,
              columnSubKey,
              this.state.tableType === TableType.ProviderTerminations ? searchString : "",
              this.state.tableType === TableType.ProviderTerminations ? selectedEffectivenessValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedEfficiencyValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedVolumeLabels : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedLineOfBusiness : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedLocationConfidenceValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedMotiveAppropriatenessValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedNewPatientClaimsValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedTopProcedures : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedCareJourneyCostValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedCareJourneyQualityValues : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedTopConditions : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedSpecialtyPredictionModelFilter : undefined,
              this.state.tableType === TableType.ProviderTerminations ? selectedPgrConfidenceTiers : undefined
            );

            // Only refresh Network when the Terminated view is now empty and no search is applied, since only then does it fall back to Network.
            fellBackToNetwork = shouldRefreshNetworkList && this.state.tableType === TableType.ProviderTerminations && !searchString && termedProviders.length === 0;
            if (fellBackToNetwork) {
              servicingProviderResult = await fetchNetworkProviders(true);
            }
          }

          //Update the servicingProviderResult providers to have isTerminated set to the existence of the provider id in the list of termed providers.
          const termedProviderIdSet = new Set(termedProviders.map((termedProvider) => termedProvider.providerId));
          servicingProviderResult.providers.forEach((provider) => provider.isTerminated = termedProviderIdSet.has(provider.providerId));
        }

        if (requestSequence !== this.latestRequestSequence) {
          return;
        }

        // Confidence Levels default to [], so check length; !! on an array is always true.
        const isFiltered =
          !!searchString
          || !!selectedVolumeLabels
          || !!selectedLineOfBusiness
          || !!selectedEffectivenessValues
          || !!selectedEfficiencyValues
          || !!selectedLocationConfidenceValues
          || !!selectedMotiveAppropriatenessValues
          || !!selectedNewPatientClaimsValues
          || selectedPgrConfidenceTiers?.length > 0;

        if (servicingProviderResult && servicingProviderResult.providers) {
          if (servicingProviderResult.providers.length > 0 || isFiltered || termedProviders?.length > 0 || forceLoad) {
            if (forceLoad) {
              newRecords = servicingProviderResult.providers;
            } else {
              newRecords = [...this.state.sortedRecords, ...servicingProviderResult.providers];
            }
            loadStatus = "loaded";
            servicingProviderResult.providers.forEach((provider) => {
              provider.cargoData.forEach((cd) => {
                this.cargoColumns.add(cd.name);
              });
            });
          } else {
            newRecords = this.state.sortedRecords.map((r) => r);
            loadStatus = "empty";
          }
        }

        noServicingProviders = servicingProviderResult.totalCount === 0;
        this.networkTotalCount = servicingProviderResult.totalCount;
        const userInitiated = this.isUserInitiatedTableTypeSwitch;
        this.isUserInitiatedTableTypeSwitch = false;
        const newTableType =
          this.props.nextClosestProviders ? TableType.NextClosestNetworkProviders :
            fellBackToNetwork ? TableType.NetworkProviders :
              isFiltered ? this.state.tableType :
              !userInitiated && noServicingProviders && termedProviders?.length > 0 ?
                TableType.ProviderTerminations :
                this.state.tableType;

        this.setState((prevState) => ({
          ...prevState,
          ...servicingProviderResult,
          accessStandardText: this.props.nextClosestProviders && (servicingProviderResult as NextClosestProviderResult).accessStandardText,
          lastSearchValue: searchString,
          lastSelectedCareJourneyCostValues: selectedCareJourneyCostValues,
          lastSelectedCareJourneyQualityValues: selectedCareJourneyQualityValues,
          lastSelectedConditions: selectedTopConditions,
          lastSelectedEffectivenessValues: selectedEffectivenessValues,
          lastSelectedEfficiencyValues: selectedEfficiencyValues,
          lastSelectedLineOfBusiness: selectedLineOfBusiness,
          lastSelectedLocationConfidenceValues: selectedLocationConfidenceValues,
          lastSelectedMotiveAppropriatenessValues: selectedMotiveAppropriatenessValues,
          lastSelectedNewPatientClaimsValues: selectedNewPatientClaimsValues,
          lastSelectedPgrConfidenceTiers: selectedPgrConfidenceTiers ?? [],
          lastSelectedProcedures: selectedTopProcedures,
          lastSelectedSpecialtyPredictionModelFilter: selectedSpecialtyPredictionModelFilter,
          lastSelectedVolumeLabels: selectedVolumeLabels,
          lastSortedCargo: columnSubKey,
          lastSortedColumn: selectedColumn,
          lastSortedDirection: sortDirection,
          loadStatus,
          offset: offset + (servicingProviderResult.batchCount || this.batchCount),
          showAverageDistance: this.props.nextClosestProviders && (servicingProviderResult as NextClosestProviderResult).showDistance,
          showAverageTime: this.props.nextClosestProviders && (servicingProviderResult as NextClosestProviderResult).showTime,
          sortedRecords: newRecords,
          tableDataProviders: this.buildTable(newRecords),
          tableDataTerminations: this.buildTable(termedProviders),
          tableType: newTableType,
          termedRecords: termedProviders,
          topConditions: prevState.topConditions,
          topProcedures: prevState.topProcedures,
          totalCount: (newTableType === TableType.NetworkProviders || newTableType === TableType.NextClosestNetworkProviders) ? servicingProviderResult.totalCount : termedProviders.length,
        }), () => {
          this.setState({
            columnConfig: this.getColumnConfig(),
          });
        });
      }
    } catch (ex) {
      if (requestSequence !== undefined && requestSequence !== this.latestRequestSequence) {
        return;
      }
      this.setState((prevState) => ({
        ...prevState,
        loadStatus: "error",
        sortedRecords: new Array<ServicingProvider>(),
        tableDataProviders: new Array<NetworkProviderTableDataWithCargo>(),
        tableDataTerminations: new Array<NetworkProviderTableDataWithCargo>(),
        termedRecords: new Array<NetworkServicingProviderTerminationModel>(),
      }));
    }
  }

  private getProviderProfile = (provider: ServicingProvider): React.ReactNode => {
    return (
      <ServicingProviderProfile
        {...this.props}
        isTerminated={this.state.tableType === TableType.ProviderTerminations}
        nextClosestProvider={this.props.nextClosestProviders}
        provider={provider}
        providerNetworkType={ProviderNetworkTypeEnum.Network}
        isZipProject={this.isZipProject}
      />
    );
  }

  private handleOnClose = () => {
    this.setState({ isOpen: false });
  }

  private handleOnOpen = () => {
    this.setState({ isOpen: true });
  }

  private handleProviderSearch = (
    searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedTopConditions?: string[],
    selectedSpecialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[]
  ): void => {
    this.buildServicingProviders(
      true,
      this.state.lastSortedColumn,
      this.state.lastSortedDirection,
      this.state.lastSortedCargo,
      true,
      searchValue,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedVolumeLabels,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues,
      selectedTopProcedures,
      selectedCareJourneyCostValues,
      selectedCareJourneyQualityValues,
      selectedTopConditions,
      selectedSpecialtyPredictionModelFilter,
      selectedPgrConfidenceTiers,
      // Network is not refetched with filters while Terminated is shown, so refresh it if the view falls back to Network.
      this.state.tableType === TableType.ProviderTerminations
    );
  }

  private handleConfidenceTierSearch = (searchValue: string, selectedPgrConfidenceTiers: number[]): void => {
    this.handleProviderSearch(
      searchValue,
      undefined, undefined, undefined, undefined, undefined, undefined, undefined,
      undefined, undefined, undefined, undefined, undefined, undefined,
      selectedPgrConfidenceTiers
    );
  }

  private get providerSearchDescriptiveText(): string {
    return `This will search each provider's name, NPI,${this.props.user.roles.includes(Roles.TIN_VIEWER) ? " TIN," : ""} specialty, and address.`;
  }

  private updateChangeTracker = () => {
    if (this.state.tableTypeChanged) {
      this.setState({
        tableTypeChanged: false,
      });
    }
  }

  private renderSearchField = (): JSX.Element => {
    if (this.props.project.showPciData) {
      return (
        <ProviderClaimsFilters
          clearFilters={this.state.tableTypeChanged}
          handleProviderSearch={this.handleProviderSearch}
          lastSearchValue={this.state.lastSearchValue}
          lastSelectedCareJourneyCostValues={this.state.lastSelectedCareJourneyCostValues}
          lastSelectedCareJourneyQualityValues={this.state.lastSelectedCareJourneyQualityValues}
          lastSelectedConditionValues={this.state.lastSelectedConditions}
          lastSelectedEffectivenessValues={this.state.lastSelectedEffectivenessValues}
          lastSelectedEfficiencyValues={this.state.lastSelectedEfficiencyValues}
          lastSelectedGainsPercent={null}
          lastSelectedLineOfBusiness={this.state.lastSelectedLineOfBusiness}
          lastSelectedVolumeLabels={this.state.lastSelectedVolumeLabels}
          lastSelectedLocationConfidenceValues={this.state.lastSelectedLocationConfidenceValues}
          lastSelectedMotiveAppropriatenessValues={this.state.lastSelectedMotiveAppropriatenessValues}
          lastSelectedNewPatientClaimsValues={this.state.lastSelectedNewPatientClaimsValues}
          lastSelectedProcedureValues={this.state.lastSelectedProcedures}
          tableType={this.state.tableType}
          validLoadStatus={this.state.loadStatus === "loaded" || this.state.loadStatus === "empty"}
          showLocationConfidence={this.props.project.showLocationConfidenceScores && this.props.runConfiguration.showLocationConfidenceScores}
          updateChangeTracker={this.updateChangeTracker}
          showCareJourneyScores={this.props.runConfiguration.showCareJourneyScores}
          showSpecialtyPredictionModel={this.props.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL)}
          lastSelectedSpecialtyPredictionModelFilter={this.state.lastSelectedSpecialtyPredictionModelFilter}
          topConditions={this.state.topConditions}
          topProcedures={this.state.topProcedures}
          lastSelectedConfidenceTierValues={this.state.lastSelectedPgrConfidenceTiers}
          showConfidenceTier={this.showPgrConfidenceTier()}
        />
      );
    } else if (this.showPgrConfidenceTier()) {
      return (
        <ConfidenceTierSearchFilters
          clearSearch={this.state.tableTypeChanged}
          descriptiveText={this.providerSearchDescriptiveText}
          handleProviderSearch={this.handleConfidenceTierSearch}
          lastSearchValue={this.state.lastSearchValue}
          lastSelectedConfidenceTierValues={this.state.lastSelectedPgrConfidenceTiers}
          tableType={this.state.tableType}
          updateChangeTracker={this.updateChangeTracker}
          validLoadStatus={this.state.loadStatus === "loaded" || this.state.loadStatus === "empty"}
        />
      );
    } else {
      return (
        <SearchBox
          key={this.state.tableType}
          descriptiveText={this.providerSearchDescriptiveText}
          fieldsetClass="d-flex"
          inputLabel="Search Providers"
          testId={`${this.state.tableType}-provider-search`}
          onSubmit={(value: string) => { this.handleProviderSearch(value) }}
        />
      );
    }
  }

  private handleLoadMore = (justChecking: boolean) => {
    if (!justChecking) {
      if (this.state.tableType === TableType.ProviderTerminations) {
        this.setState((prevState) => ({
          offset: prevState.offset + this.batchCount,
        }));
        return (this.state.offset + this.batchCount) < this.state.termedRecords.length;
      } else {
        this.buildServicingProviders(false, this.state.lastSortedColumn, this.state.lastSortedDirection, this.state.lastSortedCargo, false);
      }
      // if we're going to be returning more providers, we need to consider the additional batchCount in the total
      return (this.state.offset + this.batchCount) < this.state.totalCount;
    }
    // but if we're "just checking" to see if we should render the button, we don't need to consider the addition
    if (this.state.tableType === TableType.ProviderTerminations) {
      return this.state.offset < this.state.termedRecords.length;
    } else {
      return this.state.offset < this.state.totalCount;
    }
  }

  private renderProviderTable = () => {
    const calculationMethod = this.isZipProject ? this.calculationMethodEnumToCalculationMethod(this.props.calculationMethod) : this.props.accessSummary?.calculationMethod;
    const calcMethodText = this.props.accessSummary || this.isZipProject
      ? this.getDisplayTextForCalculationMethod(calculationMethod)
      : null;

    let leadText: JSX.Element = null;

    if (this.props.nextClosestProviders) {
      leadText = (<p>The following are based on members without access. These Providers are outside access standard of {this.state.accessStandardText}.</p>);
    } else if (calcMethodText) {
      leadText = (<p>The following network providers are within the {calcMethodText} standards.</p>);
    }

    const hasTermedProviders = (this.props.runConfiguration.isLatestPublished || this.props.runConfiguration.datePublished == null) && this.state.termedRecords.length > 0;
    const isTermedTimeslice = this.props.runConfiguration.isTermed || this.props.runConfiguration.isProjectedTermed;
    const isShowingTerminations = this.state.tableType === TableType.ProviderTerminations;
    const zipTerminationEnabled = !this.isZipProject || this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);

    return (
      <>
        <div className="d-flex justify-content-between">
          {leadText}
          {!this.props.nextClosestProviders && zipTerminationEnabled && isTermedTimeslice && (
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
              {/* Follows the filtered terminated list, as Targeted/Excluded do on Market/Prospect. */}
              {hasTermedProviders && (
                <Toggle
                  label="Terminated"
                  active={isShowingTerminations}
                  onChange={() => {
                    this.setDisplayedProviderType(isShowingTerminations ? TableType.NetworkProviders : TableType.ProviderTerminations);
                  }}
                  disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
                  containerClassName="me-3"
                  testId="network-providers-panel__toggle-terminated-providers"
                />
              )}
            </RoleRestricted>
          )}
        </div>
        {this.renderTable()}
      </>
    );
  }

  private renderTable = () => {
    return (
      <Waffles<NetworkProviderTableDataWithCargo>
        bulkActionBar={this.bulkActionHeader()}
        columns={this.state.columnConfig}
        customSort={this.handleSort}
        data={this.state.tableType === TableType.ProviderTerminations ? this.state.tableDataTerminations.slice(0, this.state.offset) : this.state.tableDataProviders}
        fullWidth={true}
        key={this.state.tableType} // change the key on table type change so it resets the internal sort direction
        loading={this.state.loadStatus !== "loaded" && this.state.loadStatus !== "empty"}
        loadMore={this.handleLoadMore}
        handleCheckboxChange={this.onCheckboxChange}
        passThroughSorting={true}
        selectedKeys={this.state.selectedProviders.map((provider) => provider.providerId.toString())}
        showCheckboxes={true}
        testId="adequacy-network-providers"
        totalNumberOfRecords={this.state.totalCount}
      />
    );
  }

  private getContractEntityFlyoutLink = (professionalAffiliationId: number, professionalContractEntityName: string, providerAffiliationVersionId: number, providerEntityType: string): JSX.Element => {
    return (
      <FlyoutLink
        content={{
          body:
            <ContractEntityProfile
              showPerformanceOverview={this.props.project.showPciData}
              contractEntityId={professionalAffiliationId ?? 0}
              contractEntityName={professionalContractEntityName}
              providerEntityType={providerEntityType}
              versionId={providerAffiliationVersionId} />,
          title: "Contract Entity Profile",
        }
        }
        classNames="qes-button-secondary"
        testId="mange-templates-list__template-history-flyout"
      >
        {professionalContractEntityName}
      </FlyoutLink>
    )
  }

  // ServicingProvider[] works for the param because NetworkServicingProviderTerminationModel extends ServicingProvider, and all its extra properties just go in the terminationReason field
  private buildTable = (inputRecords: ServicingProvider[] = this.state.tableType === TableType.ProviderTerminations ? this.state.termedRecords : this.state.sortedRecords) => {
    const viewableFields = this.props.viewableFieldsByTableType.get(this.state.tableType);
    if (!viewableFields?.length) {
      return [];
    }

    const tableData: NetworkProviderTableDataWithCargo[] = inputRecords.map((provider) => {
      const providerCargoData: Partial<NetworkProviderTableDataWithCargo> = getCargoDataForProvider(provider, viewableFields, this.cargoColumns);
      const checkIcon = <QesIcon iconName={QesIcons.Check} isDecorative className="align-text-top" />;
      const zipTerminationEnabled = !this.isZipProject || this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);

      /* oxlint-disable sort-keys */

      return ({
        [wafflesKeyColumnName]: `${provider.providerId}`,
        displayName: restrictByFields(viewableFields, [NPF.NPI, NPF.DisplayName, NPF.TIN, NPF.OrganizationName, NPF.EntityType], (has) => (
          <>
            {has(NPF.DisplayName) && (
              <FlyoutLink
                classNames="p-0 qes-button-secondary"
                content={{
                  body: this.getProviderProfile(provider) as JSX.Element,
                  title: ACCESS_POINT_PROFILE_FLYOUT_TITLE,
                }}
                testId={`network-providers-panel__provider-profile-flyout--${provider.providerId}`}
              >
                <QesIcon iconName={getEntityIcon(provider.entityType)} isDecorative className="align-text-top me-3" />
                {provider.displayName}
                {provider.isTerminated && zipTerminationEnabled && (
                  <TooltipWrapper
                    tooltipWidth="24rem"
                    text={
                      "This record is in your network and contributes to your adequacy results in the latest timeslice with a date. " +
                      "The Termination timeslice shows adequacy results without this record. If you no longer want to do a termination " +
                      "analysis on this record, go to the Termination timeslice and remove the Termination status from this record."
                    }
                    style={{ paddingLeft: "16px" }}
                  >
                    <QesIcon
                      iconName={QesIcons.UserXV1}
                      description="This provider record has been added to the current termination analysis."
                      width="16"
                      className="supress-panel-styles align-baseline" />
                  </TooltipWrapper>
                )}
              </FlyoutLink>
            )}
            {has(NPF.NPI) && !!provider.npi && (
              <span className="text-muted ml15 d-block">NPI: {provider.npi}</span>
            )}
            <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
              {has(NPF.TIN) && !!provider.tin && !!provider.tin && viewableFields.find((field) => field.backingFieldName === NPF.TIN).visible && (
                <span className="text-muted ml15 d-block">TIN: {provider.tin}</span>
              )}
            </RoleRestricted>
            {has(NPF.OrganizationName)
              && !!provider.organizationName
              && provider.entityType === EntityTypeEnum.Individual && (
                <span className="text-muted ml15 d-block">{provider.organizationName}</span>
              )}
          </>
        )),

        averageDistance: (provider as NextClosestProvider).averageDistance,
        averageTime: (provider as NextClosestProvider).averageTime,
        isServicing: (provider as NextClosestProvider).isServicing,
        memberCount: (provider as NextClosestProvider).memberCount,

        specialtyCode: provider.specialtyCode,
        specialty: provider.specialty,

        address: restrictByFields(viewableFields, [NPF.Address, NPF.Address2, NPF.City, NPF.State, NPF.Zip, NPF.Phone, NPF.Fax], (has) => (
          <>
            {has(NPF.Address) && provider.address ? <>{provider.address}<br /></> : null}
            {has(NPF.Address2) && provider.address2 ? <>{provider.address2}<br /></> : null}
            {has(NPF.City) && provider.city}
            {has(NPF.City) && (has(NPF.State) || has(NPF.Zip)) ? ", " : null}
            {has(NPF.State) && provider.state} {has(NPF.Zip) && provider.zip}
            {has(NPF.Phone) && provider.phone ? <br /> : null}
            {has(NPF.Phone) && provider.phone ?
              <>
                <TooltipWrapper text="Phone" position="bottom">
                  <QesIcon iconName={QesIcons.PhoneV1} description="Phone number" className="align-text-top me-1" />
                </TooltipWrapper>
                &nbsp; {formatPhoneNumber(provider.phone)}
              </> :
              null
            }
            {has(NPF.Fax) && provider.fax ? <br /> : null}
            {has(NPF.Fax) && provider.fax ?
              <>
                <TooltipWrapper text="Fax" position="bottom">
                  <QesIcon iconName={QesIcons.Printer} description="Fax number" className="align-text-top me-1" />
                </TooltipWrapper>
                &nbsp; {formatPhoneNumber(provider.fax)}
              </> :
              null
            }
          </>
        )),
        pgrConfidenceTierDisplay: provider.pgrConfidenceTier != null
          ? <ConfidenceTierBadge confidenceTier={provider.pgrConfidenceTier} testId="pgr-confidence-tier-badge" />
          : "---",

        prefix: provider.prefix,
        firstName: provider.firstName,
        middleName: provider.middleName,
        lastName: provider.lastName,
        suffix: provider.suffix,
        credentials: provider.credentials,
        gender: provider.gender,
        servesPercent: provider.servesPercent !== undefined ? `${provider.servesPercent.toFixed(1)}%` : "",
        availability: restrictByFields(viewableFields, [NPF.DeactivatedNPI, NPF.MedicareOptOut, NPF.OIGExcluded, NPF.OIGWaiver, NPF.Precluded, NPF.InvalidNPI, NPF.MissingNPI], (has) => (
          <>
            {has(NPF.DeactivatedNPI) && provider.deactivatedNPI && <><span>{checkIcon} Deactivated</span><br /></>}
            {has(NPF.MedicareOptOut) && provider.medicareOptOut && <><RoleRestricted allowedRoles={[Roles.MEDICARE_OPT_OUTS]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} Medicare Opt-Out</span></RoleRestricted><br /></>}
            {has(NPF.OIGExcluded) && provider.oigExcluded && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} OIG Excluded</span></RoleRestricted><br /></>}
            {has(NPF.OIGWaiver) && provider.oigWaiver && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} OIG Waiver</span></RoleRestricted><br /></>}
            {has(NPF.Precluded) && provider.precluded && <><RoleRestricted allowedRoles={[Roles.PRECLUDED_PROVIDER_USER]} deniedRoles={[Roles.TOKEN_USER]}><span>{checkIcon} Medicare Precluded</span></RoleRestricted><br /></>}
            {has(NPF.InvalidNPI) && provider.invalidNPI && <><span>{checkIcon} Invalid NPI</span><br /></>}
            {has(NPF.MissingNPI) && provider.missingNPI && <><span>{checkIcon} Missing NPI</span><br /></>}
          </>
        )),
        taxonomyCode: provider.taxonomyCode,
        acceptingNewPatients: provider.acceptingNewPatients === undefined ? "---" : provider.acceptingNewPatients ? "Y" : "N",
        employmentStatus: provider.employmentStatus,
        contractType: provider.contractType,
        underContract: provider.underContract,
        medGroupAffiliation: provider.medGroupAffiliation,
        medicareCCN: provider.medicareCCN,
        medicareCertifiedBeds: provider.medicareCertifiedBeds,
        acceptsMedicareAssignment: provider.acceptsMedicareAssignment && "Yes",
        externZip: provider.externZip,
        externSpecialtyCode: provider.externSpecialtyCode,
        externRowId: provider.externRowId,
        externProviderId: provider.externProviderId,
        externLocationId: provider.externLocationId,
        letterOfIntent: provider.letterOfIntent !== undefined ? provider.letterOfIntent ? "Y" : "N" : "---",
        daysSinceLastAttested: provider.daysSinceLastAttested && provider.daysSinceLastAttested <= this.props.runConfiguration.recentAttestationDays ?
          `${provider.daysSinceLastAttested} days` :
          "Not Attested",
        isRecentlyAttested: provider.isRecentlyAttested ? "Y" : "N",
        lastAttestedDate: provider.lastAttestedDate ? formatUnconvertedDateCustom(provider.lastAttestedDate, "YYYY-MM-DD") : "---",
        nppesCertificationDate: provider.nppesCertificationDate ? formatUnconvertedDateCustom(provider.nppesCertificationDate, "YYYY-MM-DD") : "",
        nppesLastUpdatedDate: provider.nppesLastUpdatedDate ? formatUnconvertedDateCustom(provider.nppesLastUpdatedDate, "YYYY-MM-DD") : "",
        latitude: provider.latitude,
        longitude: provider.longitude,
        geoInfoDisplay: provider.geoInfoDisplay,
        countyFIPS: provider.countyFIPS ? provider.countyFIPS : "---",
        countySSA: provider.countySSA,
        countyName: provider.countyName,
        countyState: provider.countyState,
        mipsScore: provider.mipsScore ? provider.mipsScore.toString() : "---",
        effectivenessRank: provider.effectivenessRank ? provider.effectivenessRank.toString() : "---",
        efficiencyRank: provider.efficiencyRank ? provider.efficiencyRank.toString() : "---",
        commercialClaimsVolumeDisplay: provider.commercialClaimsVolumeDisplay ? provider.commercialClaimsVolumeDisplay : "---",
        commercialNewPatientClaims: provider.commercialClaimsVolumeDisplay ? (provider.commercialNewPatientClaims ? provider.commercialNewPatientClaims : "Insufficient") : "---",
        medicaidClaimsVolumeDisplay: provider.medicaidClaimsVolumeDisplay ? provider.medicaidClaimsVolumeDisplay : "---",
        medicaidNewPatientClaims: provider.medicaidClaimsVolumeDisplay ? (provider.medicaidNewPatientClaims ? provider.medicaidNewPatientClaims : "Insufficient") : "---",
        medicareClaimsVolumeDisplay: provider.medicareClaimsVolumeDisplay ? provider.medicareClaimsVolumeDisplay : "---",
        medicareNewPatientClaims: provider.medicareClaimsVolumeDisplay ? (provider.medicareNewPatientClaims ? provider.medicareNewPatientClaims : "Insufficient") : "---",
        vaClaimsVolumeDisplay: provider.vaClaimsVolumeDisplay ? provider.vaClaimsVolumeDisplay : "---",
        vaNewPatientClaims: provider.vaClaimsVolumeDisplay ? (provider.vaNewPatientClaims ? provider.vaNewPatientClaims : "Insufficient") : "---",
        tricareClaimsVolumeDisplay: provider.tricareClaimsVolumeDisplay ? provider.tricareClaimsVolumeDisplay : "---",
        tricareNewPatientClaims: provider.tricareClaimsVolumeDisplay ? (provider.tricareNewPatientClaims ? provider.tricareNewPatientClaims : "Insufficient") : "---",
        totalClaimsVolumeDisplay: provider.totalClaimsVolumeDisplay ? provider.totalClaimsVolumeDisplay : "---",
        totalNewPatientClaims: provider.totalClaimsVolumeDisplay ? (provider.totalNewPatientClaims ? provider.totalNewPatientClaims : "Insufficient") : "---",
        locationConfidenceScoresDisplay: provider.locationConfidenceScoresDisplay ? provider.locationConfidenceScoresDisplay : "---",
        specialtyPredictionModel: provider.specialtyPredictionModelDisplay ?? "---",
        motiveScore: provider.motiveScore ? provider.motiveScore : "---",
        careJourneyScoresCostScore: provider.careJourneyScoresCostScore ? provider.careJourneyScoresCostScore : "---",
        careJourneyScoresQualityScore: provider.careJourneyScoresQualityScore ? provider.careJourneyScoresQualityScore : "---",
        professionalContractEntityName: provider.professionalContractEntityName && provider.totalClaimsVolumeDisplay !== "Ghost" ? this.getContractEntityFlyoutLink(provider.professionalAffiliationId, provider.professionalContractEntityName, this.props.runConfiguration.providerAffiliationVersionId, provider.entityType) : <>---</>,
        contractEntitySubType: provider.contractEntitySubType ? provider.contractEntitySubType : "---",
        institutionalContractEntityName: provider.institutionalContractEntityName && provider.totalClaimsVolumeDisplay !== "Ghost" ? provider.institutionalContractEntityName : "---",
        recordingCriteria: (provider as NetworkServicingProviderTerminationModel).recordingCriteria ? (provider as NetworkServicingProviderTerminationModel).recordingCriteria : "---",
        terminationReason: (() => {
          const providerTermination = provider as NetworkServicingProviderTerminationModel;

          let terminationString = "";
          if (providerTermination.terminateByContractEntity) {
            terminationString += "Contract Entity";
          }
          else if (!providerTermination.npi && !providerTermination.tin) {
            terminationString += "Name";
          }
          else if (providerTermination.terminateByNPI) {
            terminationString += "NPI";
            if (providerTermination.terminateByTIN) {
              terminationString += " and TIN";
            }
          } else if (providerTermination.terminateByTIN) {
            terminationString += "TIN";
          }
          return (
            <>
              <div className="mb-3">
                {!providerTermination.processed && <div className="demibold">UNPROCESSED</div>}
                {(this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && this.props.runConfiguration.isLatestPublished) && (
                  <TooltipWrapper text="Edit/View Terminated Provider Details" disableOnFocus={true}>
                    <FlyoutLink
                      classNames="text-info fw-normal p-0"
                      content={{
                        body: this.updateOrViewTerminatedProviderPanel(providerTermination, true),
                        title: "Termination Details",
                      }}
                      testId={`network-providers-panel__termination-details-flyout--edit--${provider.providerId}`}
                    >
                      {providerTermination.reasonName}
                    </FlyoutLink>
                  </TooltipWrapper>
                )}
                {((!this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && this.props.user.roles.includes(Roles.TERM_PROVIDER_VIEWER)) || (this.props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && this.props.runConfiguration.datePublished == null)) && (
                  <TooltipWrapper text="View Terminated Provider Details" disableOnFocus={true}>
                    <FlyoutLink
                      classNames="text-info fw-normal p-0"
                      content={{
                        body: this.updateOrViewTerminatedProviderPanel(providerTermination, false),
                        title: "Termination Details",
                      }}
                      testId={`network-providers-panel__termination-details-flyout--no-edit--${provider.providerId}`}
                    >
                      {providerTermination.reasonName}
                    </FlyoutLink>
                  </TooltipWrapper>
                )}
              </div>
              <div>
                Terminated by {terminationString}
                {providerTermination.allLocations && <div>{checkIcon} All Locations</div>}
                {providerTermination.allSpecialties && <div>{checkIcon} All Specialties</div>}
              </div>
            </>
          );
        })(),
        providerGrouping: provider.providerGrouping ? provider.providerGrouping : "---",
        providerValidations: (() => {
          const val = provider.providerValidations;
          if (!val || val === "---") return "---";
          const items = val.split(", ");
          if (items.length <= 3) return val;
          const truncated = `${items.slice(0, 3).join(", ")}…`;
          return <TooltipWrapper text={val} tooltipWidth="350px">{truncated}</TooltipWrapper>;
        })(),
        ...providerCargoData,
      });
    });
    /* oxlint-enable sort-keys */

    return tableData;
  }

  private onCheckboxChange = (newSelectedKeys: string[], isBulk?: boolean) => {
    const currentList = (this.state.tableType === TableType.NetworkProviders || this.state.tableType === TableType.NextClosestNetworkProviders) ? this.state.sortedRecords : this.state.termedRecords;
    let newSelectedProviders = [...this.state.selectedProviders];

    if (isBulk) {
      if (newSelectedKeys.length > 0) {
        newSelectedProviders.push(...currentList.filter((provider) => newSelectedKeys.includes(provider.providerId.toString())));
      } else {
        const removedIds = currentList.map((provider) => provider.providerId);
        newSelectedProviders = newSelectedProviders.filter((provider) => { return !removedIds.includes(provider.providerId); });
      }
    } else {
      if (newSelectedKeys.length > this.state.selectedProviders.length) {
        // one was added
        const addedKey = newSelectedKeys.filter((key) => !this.state.selectedProviders.find((provider) => provider.providerId.toString() === key))[0];
        // since the user clicked its box, it's safe to assume that provider is currently in memory
        const addedProvider = currentList.find((record) => record.providerId.toString() === addedKey);
        newSelectedProviders.push(addedProvider);
      } else if (newSelectedKeys.length < this.state.selectedProviders.length) {
        // one was removed
        const removedIndex = newSelectedProviders.findIndex((provider) => !newSelectedKeys.includes(provider.providerId.toString()));
        newSelectedProviders.splice(removedIndex, 1);
      }
    }
    // now we need to dedupe the array of objects...
    newSelectedProviders = newSelectedProviders.reduce((result, current) => {
      if (!result.find((r) => r.providerId === current.providerId)) {
        result.push(current);
      }
      return result;
    }, new Array<ServicingProvider | NetworkServicingProviderTerminationModel>());

    this.setState({
      selectedProviders: newSelectedProviders,
    }, () => dismiss(this.toastId));
  }

  private handleSort = (records: NetworkProviderTableDataWithCargo[], column: keyof NetworkProviderTableDataWithCargo, direction: SortDirections) => {
    this.buildServicingProviders(true, this.state.columnConfig[column].cargoTitle ? "CargoData" : column, direction, this.state.columnConfig[column].cargoTitle);
    return records;
  }

  private setDisplayedProviderType = (providerType: TableType) => {
    const newTableType: TableType = this.props.nextClosestProviders ?
      TableType.NextClosestNetworkProviders :
      providerType === TableType.NetworkProviders ? TableType.NetworkProviders : TableType.ProviderTerminations;
    this.isUserInitiatedTableTypeSwitch = true;
    this.setState({
      lastSearchValue: "",
      // Only a toggle click clears the filter row; an automatic fallback keeps the applied filters.
      tableTypeChanged: true,
      lastSelectedCareJourneyCostValues: undefined,
      lastSelectedCareJourneyQualityValues: undefined,
      lastSelectedEffectivenessValues: undefined,
      lastSelectedEfficiencyValues: undefined,
      lastSelectedLineOfBusiness: undefined,
      lastSelectedLocationConfidenceValues: undefined,
      lastSelectedMotiveAppropriatenessValues: undefined,
      lastSelectedNewPatientClaimsValues: undefined,
      lastSelectedVolumeLabels: undefined,
      offset: 0,
      tableType: newTableType,
      totalCount: providerType === TableType.NetworkProviders ? (this.networkTotalCount || this.state.sortedRecords.length) : this.state.termedRecords.length,
    }, () => {
      this.buildServicingProviders(true);
    });
  }

  private calculationMethodEnumToCalculationMethod(calculationMethodEnum: CalculationMethodEnum): CalculationMethod {
    switch (calculationMethodEnum) {
      case CalculationMethodEnum.Time:
        return CalculationMethod.Time;
      case CalculationMethodEnum.TimeAndDistance:
        return CalculationMethod.TimeAndDistance;
      case CalculationMethodEnum.TimeOrDistance:
        return CalculationMethod.TimeOrDistance;
      default:
        return CalculationMethod.Distance;
    }
  }

  private getDisplayTextForCalculationMethod(calculationMethod: CalculationMethod): string {
    switch (calculationMethod) {
      case CalculationMethod.Time:
        return "time";
      case CalculationMethod.TimeAndDistance:
      case CalculationMethod.TimeOrDistance:
        return "time/distance";
      default:
        return "distance";
    }
  }

  private createTerminateProvidersPanel = () => {
    return (
      <WrappedTerminateProvidersSimulationPanel
        onSubmit={() => {
          if (!this.isZipProject)
            this.props.setProjectHasUnprocessedTerminations(true);
          else
            this.props.zipContextEvents.onUpdateUnprocessedTerminations(true);
          this.setState({
            loadStatus: "shouldLoad",
            offset: 0,
            selectedProviders: []
          });
        }}
        projectId={this.props.project.projectId}
        providerNetworkType={ProviderNetworkTypeEnum.Network}
        selectedProviders={this.state.selectedProviders}
        specialtyGroupCode={this.props.specialtyGroupCode}
        terminatePanelMode={TerminatePanelMode.Create}
        regionSetId={this.props.regionSetId}
        isZipProject={this.isZipProject}
        showAffiliationsInsights={this.props.project.showAffiliationsInsights}
      />
    );
  }

  private updateOrViewTerminatedProviderPanel = (terminatedProvider: NetworkServicingProviderTerminationModel, canEdit: boolean) => {
    return (
      <TerminateProvidersPanel
        onSubmit={() => {
          this.setState({
            loadStatus: "shouldLoad",
            offset: 0,
            selectedProviders: []
          });
        }}
        projectId={this.props.project.projectId}
        selectedProviders={this.state.selectedProviders}
        specialtyGroupCode={this.props.specialtyGroupCode}
        terminatePanelMode={canEdit ? TerminatePanelMode.Edit : TerminatePanelMode.View}
        termination={terminatedProvider}
        showAffiliationsInsights={this.props.project.showAffiliationsInsights}
      />
    );
  }

  private bulkActionHeader = () => {
    const selectedMemberships: number[] = [this.props.runConfiguration.memberSetId];
    if (this.props.isDualMembershipMode) {
      // county context vs zip context
      selectedMemberships.push((this.props.compareToRunConfiguration || this.props.dualMembershipRunConfiguration).memberSetId);
    }

    const terminatedProvidersSelected = this.state.selectedProviders.some((provider) => provider.isTerminated);
    const terminationDisabledTooltipText = this.terminationDisabledToolTip(this.state.selectedProviders);
    const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(this.props.project.projectRunConfigurations, this.props.runConfiguration.projectRunConfigurationId);

    return (
      <>
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              activeModule: this.props.module,
              activeNetworkType: (this.props.nextClosestProviders ? ProviderNetworkTypeEnum.NextClosestProviders : ProviderNetworkTypeEnum.Network),
              adequacyFilters: this.props.adequacyFilters,
              availableExportTypes: this.isZipProject ? [ExportType.ZipAnalysisAdequacyServicingProvider] : [ExportType.ServicingProviders],
              disableRegionFilters: true,
              disableSpecialtyGroupFilters: true,
              exportNetworkProviderCount: this.state.selectedProviders.length > 0
                ? this.state.selectedProviders.length
                : this.state.tableType === TableType.NetworkProviders ? this.state.totalCount : this.state.termedRecords.length,
              filters: this.props.filters,
              granularity: RegionGranularity.County,
              initialNetworkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              networkTypes: this.props.nextClosestProviders ? [] : [ProviderNetworkTypeEnum.Network],
              nextClosestProviders: this.props.nextClosestProviders,
              perspective: this.props.perspective,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.projectURL,
              providerIds: this.state.selectedProviders.map((provider) => provider.providerId),
              regionSetIds: [this.props.regionSetId],
              showAppendNextClosestProviders: this.props.runConfiguration && this.props.runConfiguration.showNextClosestNetworkProviders || false,
              showAppendPatternOfCareProviders: this.props.showPatternOfCareNetworkProviders,
              specialtyGroupCodes: [this.props.specialtyGroupCode],

              // new reportcustomization
              /* oxlint-disable sort-keys */
              projectName: this.props.project.resource.name,
              isZipProject: this.isZipProject,
              showClosestProviderToMemberReport: this.props.project.showClosestProviderReport,
              defaultReportType: this.isZipProject ? ReportType.ZipAnalysisAdequacyServicingProvider : ReportType.AdequacyServicingProvider,
              defaultProviderSource: this.props.nextClosestProviders ? ProviderNetworkTypeEnum.NextClosestProviders : ProviderNetworkTypeEnum.Network,
              networkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
              secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
              selectedMemberships,
              selectedTimeslice: this.props.runConfiguration.projectRunConfigurationId, // runConfigurationId
              selectedRegionSetIds: !!this.props.adequacyResult?.details[0] ? [this.props.adequacyResult.details[0].regionSetId] : undefined,
              isCompareMode: !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
              selectedComparedTimeslice: this.props.compareToRunConfiguration?.projectRunConfigurationId, // compareToRunConfigurationId
              runFrequency: this.props.runFrequency || TimeFrequency.All,
              isViewingOpportunity: this.props.adequacyFilters.opportunity,
              showAddedProviders: !!this.props.compareToRunConfiguration,
              showMatchedProviders: !!this.props.compareToRunConfiguration,
              showRemovedProviders: !!this.props.compareToRunConfiguration,
              showPatternOfCare: this.props.project.showPatternOfCareProviders,
              hasProspectProviders: !!this.props.runConfiguration.prospectProviderSetId,
              hasMarketProviders: !!this.props.runConfiguration.marketProviderSetId,
              showAdvancedComparison: this.props.project.showAdvancedComparison,
              showExpandableSpecialties: !!this.props.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
              selectedSpecialties: !!this.props.filterSelections?.specialtyGroupCode ? [this.props.filterSelections.specialtyGroupCode.key] : [],
              zipProjectSearchParameters: this.isZipProject ? getSearchParametersFromFilterSelections(this.props.filterSelections) : undefined,
              showRegionSummaryReport: this.props.project.hasRegions,
              /* oxlint-enable */
            },
          }}
          data-testid={this.state.tableType + "__create-report-button"}
        >
          <QesIcon iconName={QesIcons.DownloadCloudV1} description="Download" className="align-text-top me-1" /> Create Report
        </Link>

        <TooltipWrapper text="Measure distance between two selected providers" containerClassName="ms-3">
          <QESButton
            qesType="secondary"
            className="px-0"
            disabled={this.state.selectedProviders.length !== 2}
            onClick={() => handleDistanceClick(this.state.selectedProviders, this.toastId)}
            qesTestId={`network-providers-panel__distance-button`}
          >
            <QesIcon iconName={QesIcons.Route} description="Distance" className="align-text-top me-1" /> Distance
          </QESButton>
        </TooltipWrapper>

        {this.props.runConfiguration.isLatestPublished
          && !this.props.project.isEditRestricted
          && !this.props.nextClosestProviders
          && (this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION) || !this.isZipProject)
          && (
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
              {this.state.tableType === TableType.NetworkProviders ?
                (
                  <TooltipWrapper
                    tooltipWidth={terminatedProvidersSelected ? "32rem" : "auto"}
                    text={terminatedProvidersSelected ? "We can't process this termination analysis because one or more records selected are already terminated (indicated by the xx icon). " +
                      "You will need to deselect all previously terminated records to run a new termination analysis. " +
                      "Any previously terminated records will still be terminated unless you go to the Terminated timeslice " +
                      "and remove the Termination status from this record."
                      : terminationDisabledTooltipText !== ""
                        ? terminationDisabledTooltipText
                        : `Terminate selected ${this.providerPlurality}`}
                    containerClassName="ms-3"
                  >
                    <FlyoutLink
                      classNames="p-0"
                      content={{
                        body: this.createTerminateProvidersPanel(),
                        title: `Terminate ${capitalizeFirstLetter(this.providerPlurality)}`,
                      }}
                      disabled={!this.state.selectedProviders.length || terminatedProvidersSelected || terminationDisabledTooltipText !== ""}
                      onClose={() => {
                        this.setState({
                          loadStatus: "shouldLoad",
                          offset: 0,
                          selectedProviders: []
                        });
                      }}
                      testId="network-providers-panel__terminate-flyout"
                    >
                      <QesIcon iconName={QesIcons.UserXV1} description="Terminate" className="align-text-top me-1" /> Terminate
                    </FlyoutLink>
                  </TooltipWrapper>
                ) :
                (
                  <TooltipWrapper text={`Restore selected ${this.providerPlurality}`} containerClassName="ms-3">
                    <ModalLink
                      content={{
                        body: `Are you sure you want to restore the selected ${this.providerPlurality}?`,
                        cancelButtonText: "Cancel",
                        confirmButtonAction: async () => {
                          await this.providerTerminationService.deleteTermination(
                            this.props.project.projectId,
                            this.state.selectedProviders.map((sp) => (sp as NetworkServicingProviderTerminationModel).providerTerminationProjectId)
                          );
                          const unprocessedProviderTerminations = await this.providerTerminationService.getUnprocessedProviderTerminations(this.props.project.projectId);

                          if (!this.isZipProject)
                            this.props.setProjectHasUnprocessedTerminations(unprocessedProviderTerminations?.length > 0);
                          else
                            this.props.zipContextEvents.onUpdateUnprocessedTerminations(unprocessedProviderTerminations?.length > 0);

                          this.setState({
                            loadStatus: "shouldLoad",
                            offset: 0,
                          }, () => { this.props.handleCloseFlyout(); });
                        },
                        confirmButtonText: "Restore",
                        title: `Restore ${capitalizeFirstLetter(this.providerPlurality)}`,
                      }}
                      testId="network-providers-panel__restore-modal"
                      disabled={!this.state.selectedProviders.length}
                      classNames="p-0"
                    >
                      <QesIcon iconName={QesIcons.UserV1} description="Restore" className="align-text-top me-1" /> Restore
                    </ModalLink>
                  </TooltipWrapper>
                )}
            </RoleRestricted>
          )}

        {this.isZipProject && (
          <FlyoutLink
            content={{
              body: (
                <DisplayOptions
                  defaultOpen={true}
                  projectId={this.props.project.projectId}
                  viewablePanels={
                    this.props.enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION)
                      ? [TableType.NetworkProviders, TableType.ProviderTerminations]
                      : [TableType.NetworkProviders]
                  }
                  onChange={(isDirty: boolean) => { this.setState({ displayOptionsDirty: isDirty }) }}
                  relevantTableType={TableType.NetworkProviders}
                />
              ),
              title: "Display Options",
            }}
            modalContent={{
              body: "You will lose unsaved changes. Continue with closing the sidebar?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            size="60"
            classNames={"qes-button-secondary text-nowrap p-0 ms-3"}
            warnOnClose={this.state.displayOptionsDirty}
            testId="network-providers-panel__viewable-fields-flyout-link"
          >
            Display Options
          </FlyoutLink>
        )}
      </>
    );
  };

  private getColumnConfig = () => {
    return buildAdequacyProvidersColumnConfig(
      this.props.viewableFieldsByTableType.get(this.state.tableType),
      this.state.tableType,
      this.props.project.showNppesDates && this.props.runConfiguration && this.props.runConfiguration.showNppesDates,
      this.props.project.showMipsScore && this.props.runConfiguration && this.props.runConfiguration.showMipsScore,
      this.props.project.showPciData && this.props.runConfiguration && this.props.runConfiguration.showPciData,
      this.props.project.showVaClaimsVolumeColumn,
      this.props.project.showTricareClaimsVolumeColumn,
      this.state.showAverageDistance,
      this.state.showAverageTime,
      false,
      false,
      false,
      false,
      this.props.project.showLocationConfidenceScores && this.props.runConfiguration && this.props.runConfiguration.showLocationConfidenceScores,
      this.props.project.showMotiveAppropriatenessScore && this.props.runConfiguration && this.props.runConfiguration?.showMotiveAppropriatenessScore,
      this.props.runConfiguration?.showCareJourneyScores,
      this.props.runConfiguration?.showAffiliationsInsights,
      this.props.runConfiguration?.affiliationsDisplayOnNetworkAccordion,
      this.props.enabledFeatureFlags.has(Features.CCIIO_DISPLAY_OPTION),
      this.props.enabledFeatureFlags.has(Features.CCIIO_DISPLAY_OPTION),
      false,
      this.props.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL),
      this.showPgrConfidenceTier()
    );
  }

  private showPgrConfidenceTier = (): boolean =>
    !!this.props.runConfiguration?.showPgrConfidenceTier
    && this.props.enabledFeatureFlags.has(Features.SHOW_CONFIDENCE_TIER_NON_MARKET_ACCESS_POINTS);

  private getAliasString = () => {
    const hideAlias = this.props.adequacyResult?.aggregateSummary?.hideAlias ?? false;
    let alias = "";

    if (hideAlias) {
      return alias;
    } else if (this.props.nextClosestProviders && this.props.runConfiguration?.nextClosestProviderSetAlias) {
      alias = ` (${this.props.runConfiguration.nextClosestProviderSetAlias})`;
    } else if (this.props.runConfiguration?.networkProviderSetAlias) {
      alias = ` (${this.props.runConfiguration.networkProviderSetAlias})`;
    }

    return alias;
  };

  private getTitle = () => {
    const aliasString = this.getAliasString();
    const records = this.state.tableType === TableType.NetworkProviders
      ? this.state.sortedRecords
      : this.state.termedRecords;

    const shouldShowBatchCount = this.state.isOpen &&
      (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading");

    const batchCount = shouldShowBatchCount
      ? ` (${this.state.sortedRecords.length} of ${this.state.totalCount}${this.state.lastSearchValue ? `, filtered to "${this.state.lastSearchValue}"` : ""})`
      : "";

    if (this.props.nextClosestProviders) {
      return `Next Closest Network Providers${aliasString}${batchCount}`;
    }

    if (this.state.tableType === TableType.ProviderTerminations) {
      const terminationsCount = ` (${Math.min(this.state.offset, records.length)} of ${records.length}${this.state.lastSearchValue ? `, filtered to "${this.state.lastSearchValue}"` : ""})`;
      return `Network Servicing Provider Terminations${aliasString}${terminationsCount}`;
    }

    return `Network Servicing Providers${aliasString}${batchCount}`;
  };

  private terminationDisabledToolTip = (selectedProviders: ServicingProvider[]) => {
    const tinViewer = this.props.user.roles.includes(Roles.TIN_VIEWER);

    if (tinViewer) {
      // A mix of NPI-only and TIN-only is selected
      if (selectedProviders.some((provider) => !provider.npi && provider.tin) && selectedProviders.some((provider) => provider.npi && !provider.tin)) {
        return "All selected providers must have NPIs or all selected providers must have TINs.";
      }
    }

    if (this.isZipProject && selectedProviders.some((provider) => !provider.npi && (!tinViewer || !provider.tin))) {
      return tinViewer
        ? "All selected providers must have an NPI or TIN to terminate."
        : "All selected providers must have an NPI to terminate.";
    }

    return "";
  };
}

const WrappedNetworkProvidersPanel = withZipContextState(withFlyoutContext(withFeatureFlagContext(withUserContext(withViewableFields(withCountyProjectContext(withZipContextEventHandlers(NetworkProvidersPanel)))))));

export { WrappedNetworkProvidersPanel as NetworkProvidersPanel };
export { NetworkProvidersPanel as NetworkProvidersPanelClass };
